import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { recordPricingRequest, unwrapData } from "layouts/record-pricing/api";

const emptyTier = { name: "", description: "", pricePerRecord: "", default: false };
const emptyCoupon = { code: "", discountPerRecord: "", expiresAt: "" };

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return "No expiration";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

function RecordPricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tierDialog, setTierDialog] = useState(null);
  const [tierForm, setTierForm] = useState(emptyTier);
  const [deleteTier, setDeleteTier] = useState(null);
  const [savingTier, setSavingTier] = useState(false);
  const [assignment, setAssignment] = useState({ userId: "", tierId: "" });
  const [assigning, setAssigning] = useState(false);
  const [couponForm, setCouponForm] = useState(emptyCoupon);
  const [couponLookupCode, setCouponLookupCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [deactivateCoupon, setDeactivateCoupon] = useState(null);
  const [notice, setNotice] = useState(null);
  const sessionId = useSelector((state) => state.login.sessionId);
  const navigate = useNavigate();

  const handleRequestError = (requestError) => {
    if (requestError.status === 401 || requestError.status === 403) {
      navigate("/authentication/sign-in");
      return;
    }
    setError(requestError.message || "The pricing request failed.");
  };

  const loadTiers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await recordPricingRequest("admin/tiers", sessionId);
      const data = unwrapData(response);
      setTiers(Array.isArray(data) ? data : data?.tiers || []);
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTiers();
    // The protected route guarantees a session before this page mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const openCreateTier = () => {
    setTierForm(emptyTier);
    setTierDialog("create");
  };

  const openEditTier = (tier) => {
    setTierForm({
      name: tier.name || "",
      description: tier.description || "",
      pricePerRecord: tier.pricePerRecord ?? "",
      default: Boolean(tier.default),
    });
    setTierDialog(tier);
  };

  const saveTier = async () => {
    if (!tierForm.name.trim() || tierForm.pricePerRecord === "") {
      setError("Tier name and price per record are required.");
      return;
    }

    setSavingTier(true);
    setError("");
    const editing = tierDialog !== "create";
    const path = editing ? `admin/tiers/${tierDialog.id}` : "admin/tiers";

    try {
      await recordPricingRequest(path, sessionId, {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({
          ...(editing ? { id: tierDialog.id } : {}),
          name: tierForm.name.trim(),
          description: tierForm.description.trim(),
          pricePerRecord: Number(tierForm.pricePerRecord),
          default: tierForm.default,
        }),
      });
      setTierDialog(null);
      setNotice({ title: editing ? "Tier updated" : "Tier created", color: "success" });
      await loadTiers();
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setSavingTier(false);
    }
  };

  const confirmDeleteTier = async () => {
    setSavingTier(true);
    setError("");
    try {
      await recordPricingRequest(`admin/tiers/${deleteTier.id}`, sessionId, {
        method: "DELETE",
      });
      setDeleteTier(null);
      setNotice({ title: "Tier deleted", color: "success" });
      await loadTiers();
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setSavingTier(false);
    }
  };

  const assignTier = async () => {
    if (!assignment.userId || !assignment.tierId) {
      setError("Select a tier and enter the user ID to assign it.");
      return;
    }

    setAssigning(true);
    setError("");
    try {
      await recordPricingRequest(
        `admin/users/${assignment.userId}/tiers/${assignment.tierId}`,
        sessionId,
        { method: "PUT" }
      );
      setNotice({ title: "Tier assigned", color: "success" });
      setAssignment({ userId: "", tierId: "" });
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setAssigning(false);
    }
  };

  const createCoupon = async () => {
    if (!couponForm.code.trim() || couponForm.discountPerRecord === "") {
      setError("Coupon code and discount per record are required.");
      return;
    }

    setCouponBusy(true);
    setError("");
    try {
      const submittedCoupon = {
        code: couponForm.code.trim(),
        discountPerRecord: Number(couponForm.discountPerRecord),
        ...(couponForm.expiresAt
          ? { expiresAt: new Date(couponForm.expiresAt).toISOString() }
          : {}),
        active: true,
      };
      const response = await recordPricingRequest("admin/coupons", sessionId, {
        method: "POST",
        body: JSON.stringify(submittedCoupon),
      });
      const createdCoupon = unwrapData(response);
      setCoupon(createdCoupon?.code ? createdCoupon : submittedCoupon);
      setCouponLookupCode(submittedCoupon.code);
      setCouponForm(emptyCoupon);
      setNotice({ title: "Coupon created", color: "success" });
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setCouponBusy(false);
    }
  };

  const findCoupon = async () => {
    if (!couponLookupCode.trim()) {
      setError("Enter a coupon code to look up.");
      return;
    }

    setCouponBusy(true);
    setError("");
    try {
      const response = await recordPricingRequest(
        `admin/coupons/${encodeURIComponent(couponLookupCode.trim())}`,
        sessionId
      );
      setCoupon(unwrapData(response));
    } catch (requestError) {
      setCoupon(null);
      handleRequestError(requestError);
    } finally {
      setCouponBusy(false);
    }
  };

  const confirmDeactivateCoupon = async () => {
    setCouponBusy(true);
    setError("");
    try {
      await recordPricingRequest(
        `admin/coupons/${encodeURIComponent(deactivateCoupon.code)}/deactivate`,
        sessionId,
        { method: "PUT" }
      );
      setCoupon((current) => ({ ...current, active: false }));
      setDeactivateCoupon(null);
      setNotice({ title: "Coupon deactivated", color: "success" });
    } catch (requestError) {
      handleRequestError(requestError);
    } finally {
      setCouponBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={5}>
        <MDBox mb={3}>
          <MDTypography variant="h4">Record pricing</MDTypography>
          <MDTypography variant="button" color="text">
            Manage per-record pricing tiers, user assignments, and coupon discounts.
          </MDTypography>
        </MDBox>

        {error && (
          <MDBox mb={3}>
            <MDAlert color="error" dismissible>
              <MDTypography variant="body2" color="white">
                {error}
              </MDTypography>
            </MDAlert>
          </MDBox>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="h6">Pricing tiers</MDTypography>
                  <MDTypography variant="caption" color="text">
                    Current per-record pricing available for assignment.
                  </MDTypography>
                </MDBox>
                <MDButton color="info" size="small" onClick={openCreateTier}>
                  <AddIcon fontSize="small" sx={{ mr: 0.75 }} /> New tier
                </MDButton>
              </MDBox>

              {loading ? (
                <MDBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress color="info" />
                </MDBox>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ display: "table-header-group" }}>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Price per record</TableCell>
                        <TableCell>Default</TableCell>
                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                          Updated
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiers.map((tier) => (
                        <TableRow key={tier.id}>
                          <TableCell>{tier.name}</TableCell>
                          <TableCell>{tier.description || "-"}</TableCell>
                          <TableCell>{formatPrice(tier.pricePerRecord)}</TableCell>
                          <TableCell>{tier.default ? "Yes" : "No"}</TableCell>
                          <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                            {tier.updatedAt ? formatDate(tier.updatedAt) : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ px: { xs: 0.5, sm: 2 }, whiteSpace: "nowrap" }}>
                            <Tooltip title="Edit tier">
                              <IconButton
                                size="small"
                                onClick={() => openEditTier(tier)}
                                aria-label="Edit tier"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete tier">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTier(tier)}
                                aria-label="Delete tier"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {tiers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No pricing tiers have been created.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6">Assign a tier</MDTypography>
                <MDTypography variant="caption" color="text">
                  Apply a pricing tier to a user account.
                </MDTypography>
                <MDBox mt={3}>
                  <MDInput
                    type="number"
                    label="User ID"
                    value={assignment.userId}
                    onChange={(event) =>
                      setAssignment((current) => ({ ...current, userId: event.target.value }))
                    }
                    fullWidth
                  />
                </MDBox>
                <MDBox mt={2}>
                  <MDInput
                    select
                    label="Pricing tier"
                    value={assignment.tierId}
                    onChange={(event) =>
                      setAssignment((current) => ({ ...current, tierId: event.target.value }))
                    }
                    fullWidth
                  >
                    {tiers.map((tier) => (
                      <MenuItem key={tier.id} value={tier.id}>
                        {tier.name} ({formatPrice(tier.pricePerRecord)})
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>
                <MDButton
                  color="info"
                  fullWidth
                  sx={{ mt: 3 }}
                  disabled={assigning}
                  onClick={assignTier}
                >
                  <PersonAddIcon fontSize="small" sx={{ mr: 0.75 }} />
                  {assigning ? "Assigning..." : "Assign tier"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6">Create a coupon</MDTypography>
                <MDTypography variant="caption" color="text">
                  Create a code that discounts the per-record price.
                </MDTypography>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      label="Coupon code"
                      value={couponForm.code}
                      onChange={(event) =>
                        setCouponForm((current) => ({ ...current, code: event.target.value }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      type="number"
                      label="Discount per record"
                      value={couponForm.discountPerRecord}
                      onChange={(event) =>
                        setCouponForm((current) => ({
                          ...current,
                          discountPerRecord: event.target.value,
                        }))
                      }
                      inputProps={{ min: 0, step: "0.000001" }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      type="datetime-local"
                      label="Expires at"
                      value={couponForm.expiresAt}
                      onChange={(event) =>
                        setCouponForm((current) => ({ ...current, expiresAt: event.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <MDButton
                  color="success"
                  sx={{ mt: 2 }}
                  disabled={couponBusy}
                  onClick={createCoupon}
                >
                  <AddIcon fontSize="small" sx={{ mr: 0.75 }} /> Create coupon
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6">Find and deactivate coupons</MDTypography>
                <MDTypography variant="caption" color="text">
                  The API supports coupon lookup by code; it does not provide a coupon list.
                </MDTypography>
                <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} mt={3}>
                  <MDInput
                    label="Coupon code"
                    value={couponLookupCode}
                    onChange={(event) => setCouponLookupCode(event.target.value)}
                    fullWidth
                  />
                  <MDButton color="info" disabled={couponBusy} onClick={findCoupon}>
                    <SearchIcon fontSize="small" sx={{ mr: 0.75 }} /> Find
                  </MDButton>
                </MDBox>

                {coupon && (
                  <MDBox
                    mt={3}
                    pt={3}
                    borderTop={({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[300]}`
                    }
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6">{coupon.code}</MDTypography>
                      <MDTypography variant="button" color="text" display="block">
                        {formatPrice(coupon.discountPerRecord)} discount per record · Expires{
                          " "
                        }
                        {formatDate(coupon.expiresAt)}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        color={coupon.active === false ? "error" : "success"}
                      >
                        {coupon.active === false ? "Inactive" : "Active"}
                      </MDTypography>
                    </MDBox>
                    {coupon.active !== false && (
                      <MDButton
                        color="error"
                        variant="outlined"
                        onClick={() => setDeactivateCoupon(coupon)}
                      >
                        <BlockIcon fontSize="small" sx={{ mr: 0.75 }} /> Deactivate
                      </MDButton>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={Boolean(tierDialog)} onClose={() => setTierDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{tierDialog === "create" ? "Create pricing tier" : "Edit pricing tier"}</DialogTitle>
        <DialogContent>
          <MDBox pt={1}>
            <MDInput
              label="Tier name"
              value={tierForm.name}
              onChange={(event) =>
                setTierForm((current) => ({ ...current, name: event.target.value }))
              }
              fullWidth
            />
          </MDBox>
          <MDBox mt={2}>
            <MDInput
              label="Description"
              value={tierForm.description}
              onChange={(event) =>
                setTierForm((current) => ({ ...current, description: event.target.value }))
              }
              multiline
              rows={3}
              fullWidth
            />
          </MDBox>
          <MDBox mt={2}>
            <MDInput
              type="number"
              label="Price per record"
              value={tierForm.pricePerRecord}
              onChange={(event) =>
                setTierForm((current) => ({ ...current, pricePerRecord: event.target.value }))
              }
              inputProps={{ min: 0, step: "0.000001" }}
              fullWidth
            />
          </MDBox>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={tierForm.default}
                onChange={(event) =>
                  setTierForm((current) => ({ ...current, default: event.target.checked }))
                }
              />
            }
            label="Default pricing tier"
          />
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setTierDialog(null)}>
            Cancel
          </MDButton>
          <MDButton color="info" disabled={savingTier} onClick={saveTier}>
            {savingTier ? "Saving..." : "Save tier"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTier)} onClose={() => setDeleteTier(null)}>
        <DialogTitle>Delete pricing tier?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            This permanently deletes {deleteTier?.name}. Users assigned to it may lose their pricing.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setDeleteTier(null)}>
            Cancel
          </MDButton>
          <MDButton color="error" disabled={savingTier} onClick={confirmDeleteTier}>
            Delete tier
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deactivateCoupon)} onClose={() => setDeactivateCoupon(null)}>
        <DialogTitle>Deactivate coupon?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            The coupon {deactivateCoupon?.code} will no longer be eligible for use.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setDeactivateCoupon(null)}>
            Cancel
          </MDButton>
          <MDButton color="error" disabled={couponBusy} onClick={confirmDeactivateCoupon}>
            Deactivate
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        open={Boolean(notice)}
        color={notice?.color || "success"}
        icon="check"
        title={notice?.title || "Saved"}
        dateTime="now"
        content="The record pricing configuration was updated."
        close={() => setNotice(null)}
        onClose={() => setNotice(null)}
      />
    </DashboardLayout>
  );
}

export default RecordPricing;